import { useEffect, useState } from "react";
import { Copy, Check, Landmark, QrCode } from "lucide-react";
import { toast } from "sonner";
import api, { imgUrl } from "@/lib/api";

const qrSrc = (path) => (path?.startsWith("/") ? path : imgUrl(path));

const CopyRow = ({ label, value, testId }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setDone(true);
    toast.success(`${label} copied`);
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0">
      <div className="min-w-0">
        <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">{label}</p>
        <p className="text-sm text-white font-mono truncate" data-testid={testId}>{value}</p>
      </div>
      <button type="button" onClick={copy} aria-label={`Copy ${label}`} data-testid={`${testId}-copy`} className="text-white/40 hover:text-[#D4AF37] transition-colors shrink-0">
        {done ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
      </button>
    </div>
  );
};

export const PaymentDetails = ({ amount }) => {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/settings/payment").then((r) => setS(r.data)).catch(() => {});
  }, []);
  if (!s) return <div className="h-32 border border-white/10 animate-pulse" data-testid="payment-details-loading" />;
  const upiLink = `upi://pay?pa=${encodeURIComponent(s.upi_id)}&pn=${encodeURIComponent(s.account_name)}${amount ? `&am=${amount}` : ""}&cu=INR`;
  return (
    <div className="border border-white/10 bg-[#0A0A0A]" data-testid="payment-details">
      <div className="grid sm:grid-cols-5">
        <div className="sm:col-span-2 bg-white p-3 flex flex-col items-center justify-center gap-2">
          <img src={qrSrc(s.qr_image)} alt={`UPI QR for ${s.upi_id}`} className="w-full max-w-[180px] object-contain" data-testid="payment-qr-image" />
          <a href={upiLink} data-testid="payment-upi-link" className="font-accent text-[9px] tracking-[0.2em] uppercase text-black/70 underline underline-offset-2 sm:hidden">Open in UPI app</a>
        </div>
        <div className="sm:col-span-3 p-4">
          <div className="flex items-center gap-2 mb-2">
            <QrCode size={13} className="text-[#D4AF37]" />
            <p className="font-accent text-[9px] tracking-[0.25em] uppercase text-[#D4AF37]">Scan or pay via UPI</p>
          </div>
          <CopyRow label="UPI ID" value={s.upi_id} testId="payment-upi-id" />
          <div className="flex items-center gap-2 mt-4 mb-2">
            <Landmark size={13} className="text-[#D4AF37]" />
            <p className="font-accent text-[9px] tracking-[0.25em] uppercase text-[#D4AF37]">Bank transfer · {s.bank_name}</p>
          </div>
          <CopyRow label="Account name" value={s.account_name} testId="payment-account-name" />
          <CopyRow label="Account number" value={s.account_number} testId="payment-account-number" />
          <CopyRow label="IFSC" value={s.ifsc} testId="payment-ifsc" />
          <p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35 mt-2">Type · <span className="text-white/60">{s.account_type}</span></p>
        </div>
      </div>
      {s.instructions && <p className="text-xs text-white/50 leading-relaxed px-4 py-3 border-t border-white/10" data-testid="payment-instructions">{s.instructions}</p>}
    </div>
  );
};
